import italyContractSource from '../../../docs/ItalyContractContent.md?raw';
import romaniaContractSource from '../../../docs/RomaniaContractContent.md?raw';
import germanyContractSource from '../../../docs/GermanyContractContent.md?raw';

/*
 * Contract text helpers shared by the student portal and the public contract
 * page. Moved unchanged from PortalContract.tsx.
 */

export type ContractSection = { title: string; paragraphs: string[] };
export const TOTAL_PAGES_TOKEN = '{{TOTAL_CONTRACT_PAGES}}';
const ROMANIA_HEADING = /^(?:ثالثًا|رابعًا|خامسًا|سادسًا|سابعًا|ثامنًا|تاسعًا|عاشرًا|حادي عشر|ثاني عشر|ثالث عشر|رابع عشر|خامس عشر|سادس عشر|سابع عشر|ثامن عشر|تاسع عشر|عشرون|حادي وعشرون|ثاني وعشرون|ثالث وعشرون|رابع وعشرون|خامس وعشرون|سادس وعشرون|سابع وعشرون|ثامن وعشرون|تاسع وعشرون|ثلاثون|حادي وثلاثون|ثاني وثلاثون|ثالث وثلاثون|رابع وثلاثون|خامس وثلاثون|سادس وثلاثون|سابع وثلاثون|ثامن وثلاثون|تاسع وثلاثون|أربعون|حادي وأربعون|ثاني وأربعون|ثالث وأربعون):/;


export const parseSections = (source: string): ContractSection[] => {
  const lines = source.split(/\r?\n/).map((line) => line.trim()).filter((line) => line && line !== '.');
  const start = lines.findIndex((line) => line === 'تمهيد' || /ثالثًا:\s*التمهيد/.test(line));
  const end = lines.findIndex((line) => line === 'التوقيعات');
  const sections: ContractSection[] = [];
  for (const line of lines.slice(start, end > start ? end : undefined)) {
    if (line === 'تمهيد' || line.startsWith('البند ') || ROMANIA_HEADING.test(line)) sections.push({ title: line, paragraphs: [] });
    else sections.at(-1)?.paragraphs.push(line);
  }
  return sections;
};

export const answerLabels: Record<string, Record<string, string>> = {
  residency: { egypt: 'مقيم في مصر', arab_country: 'طالب عربي في دولة عربية أخرى', egyptian_arab_residency: 'مصري مقيم في دولة عربية', romania_study: 'رومانيا - إقامة دراسة', romania_work: 'رومانيا - إقامة عمل', europe_study: 'دولة أوروبية - إقامة دراسة', europe_work: 'دولة أوروبية - إقامة عمل', italy: 'مقيم في إيطاليا' },
  language_status: { certificate: 'معه شهادة لغة إنجليزية معتمدة', no_certificate: 'لا يملك شهادة - يشمل العقد خدمة شهادة اللغة', speaking_good: 'بدون شهادة - المحادثة جيدة', speaking_needs_support: 'بدون شهادة - يحتاج دعم محادثة', language_and_test: 'يحتاج لغة + TOLC / CEnT' },
  study_level: { bachelor: 'بكالوريوس', master: 'ماجستير', phd: 'دكتوراه' },
  advance_payment: { yes: 'السداد الكامل مقدمًا بعد الخصم', no: 'السداد على مراحل الخدمة' },
  study_track: { preparation_year: 'سنة تحضيرية رومانية ثم الدراسة الجامعية', english: 'الدراسة مباشرة باللغة الإنجليزية' },
  english_exam: { yes: 'يرغب في أداء اختبار الإنجليزية الخاص بالشركة', no: 'لا يرغب في اختبار الإنجليزية الخاص بالشركة' },
  special_offer: { 'italy-all-inclusive-1500': 'باقة إيطاليا الشاملة (شهادة اللغة وكل الخدمات)', 'germany-plus-200': 'باقة ألمانيا الخاصة' },
};
export const answerNames: Record<string, string> = { residency: 'الإقامة', language_status: 'حالة اللغة', study_level: 'المرحلة', field: 'التخصص', intake: 'موعد البدء', budget: 'الميزانية', advance_payment: 'طريقة السداد', study_track: 'طريقة الدراسة', english_exam: 'اختبار الإنجليزية', special_offer: 'الباقة' };
export const readableAnswer = (key: string, value: unknown) => answerLabels[key]?.[String(value)] || String(value ?? '—').replaceAll('_', ' ');

export type ContractKind = 'italy' | 'romania' | 'germany';

/**
 * Which country's contract this is. The signed template decides; the country code
 * is only a fallback for templates that name no country. Title, text and file name
 * all use this, so they can never disagree (a Germany title on an Italy contract).
 */
export const contractKind = (templateKey?: string | null, countryCode?: string | null): ContractKind => {
  for (const kind of ['italy', 'romania', 'germany'] as const) if (templateKey?.startsWith(`${kind}-`)) return kind;
  const code = (countryCode || '').toUpperCase();
  if (code === 'RO' || code === 'ROU') return 'romania';
  if (code === 'DE' || code === 'DEU') return 'germany';
  return 'italy';
};

/** The contract text for a contract; Italy unless Romania or Germany. */
export const contractSourceFor = (templateKey?: string | null, countryCode?: string | null): string => {
  const kind = contractKind(templateKey, countryCode);
  return kind === 'romania' ? romaniaContractSource : kind === 'germany' ? germanyContractSource : italyContractSource;
};
